import React from 'react';
import { useIntl } from 'react-intl';
import { Menu } from 'lucide-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { X } from 'lucide-react';
import { Logo } from '../../../components/navigation';
import {
  FilterCategoryButtons,
  createFilterCategories,
  createFilterCategoriesFromLabels,
} from '../../../components/filters/FilterCategoryButtons';
import Avatar from '../../../components/Avatar';
import { StaticTopFilters } from './StaticTopFilters';
import type { SrpStaticStrings, SrpFacetsData } from '../../../features/srp/SrpViewModel';
import { getLocaleFromPathname, normalizeSupportedLocale } from '../../../utils/routing/locale-from-pathname';

const SEARCH_PLACEHOLDER_ID = 'instantsearch.searchbox.placeholder';

/**
 * Static search box: identical look to Autocomplete input, no functionality.
 * Shows query value when available, otherwise placeholder. Clear icon or "/" badge for visual parity.
 * Swapped for real Autocomplete when the page becomes interactive.
 */
function StaticSearchBox({
  query,
  placeholder,
}: {
  query: string;
  placeholder: string;
}): React.ReactElement {
  const hasValue = (query ?? '').trim().length > 0;
  const displayValue = (query ?? '').trim();

  return (
    <div className="relative w-full z-[100]" data-component="static-search-box">
      <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400 pointer-events-none z-10" />
      <div
        className="pl-11 pr-10 py-3 backdrop-blur-xl text-gray-900 dark:text-gray-100 font-medium w-full text-base transition-all duration-300 ease-out bg-white/90 dark:bg-gray-800/90 border border-gray-200/50 dark:border-gray-700/50 rounded-3xl ring-0 shadow-md font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif]"
        style={{
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.08), 0 1px 4px 0 rgba(0, 0, 0, 0.04)',
        }}
        aria-hidden
      >
        {displayValue ? (
          <span className="block truncate">{displayValue}</span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
        )}
      </div>
      {hasValue ? (
        <div
          className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100/80 dark:bg-gray-700/80 text-gray-600 dark:text-gray-300 pointer-events-none"
          style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}
          aria-hidden
        >
          <X className="w-4 h-4" />
        </div>
      ) : (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-full bg-gray-100/80 dark:bg-gray-700/80 text-gray-500 dark:text-gray-400 text-xs font-mono font-semibold select-none pointer-events-none border border-gray-200/50 dark:border-gray-600/50 backdrop-blur-sm"
          style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', opacity: 0.9 }}
          aria-hidden
        >
          /
        </span>
      )}
    </div>
  );
}

/**
 * Static SRP header using pre-generated strings (no IntlProvider).
 * Used when rendering the initial SSR static shell.
 * Renders language selector and user avatar (when logged in) to match desktop chrome.
 */
export interface StaticSRPHeaderWithStringsProps {
  /** Current locale; when missing or empty, derived from pathname so the language label is never empty. */
  locale?: string;
  query?: string;
  staticStrings: SrpStaticStrings;
  userAvatarUrl?: string | null;
  userName?: string | null;
  /** When provided, the second row (horizontally scrollable facet pills) is rendered from initial Algolia data. */
  facets?: SrpFacetsData;
  refinementList?: Record<string, string[]>;
  pathname?: string;
  indexName?: string;
}

export function StaticSRPHeaderWithStrings({
  locale,
  query,
  staticStrings,
  userAvatarUrl,
  userName,
  facets,
  refinementList = {},
  pathname = '/',
  indexName,
}: StaticSRPHeaderWithStringsProps): React.ReactElement {
  const categories = React.useMemo(
    () => createFilterCategoriesFromLabels(staticStrings.filterCategoryLabels, {}),
    [staticStrings.filterCategoryLabels]
  );
  const hasUser = !!(userAvatarUrl || userName);
  // Derive from pathname when locale is missing (static shell may render before view model has locale)
  const effectiveLocale = locale && locale.trim() ? locale : (getLocaleFromPathname(pathname) ?? 'en');
  const normalizedLocale = normalizeSupportedLocale(effectiveLocale);
  const localeCode = (normalizedLocale === 'fi' ? 'FI' : normalizedLocale === 'sv' ? 'SV' : 'EN') as 'FI' | 'SV' | 'EN';
  return (
    <header
      className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-[45]"
      role="banner"
    >
      {/* Desktop: logo, search, filter categories, language selector, menu (with optional avatar) */}
      <nav
        className="hidden lg:flex items-center gap-3 h-14 min-h-14 py-3 px-4 xl:px-20 md:px-10 border-b border-gray-200 dark:border-gray-700"
        aria-label="Main navigation"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Logo tooltipLabel={staticStrings.logoTooltip} locale={normalizedLocale} variant="horizontal" />
          <Logo tooltipLabel={staticStrings.logoTooltip} locale={normalizedLocale} variant="compact" />
          <div className="min-w-0 flex-1 max-w-lg xl:max-w-3xl w-full">
            <StaticSearchBox query={query ?? ''} placeholder={staticStrings.searchPlaceholder} />
          </div>
        </div>
        <FilterCategoryButtons
          categories={categories}
          activeCategory={null}
          onCategoryClick={() => {}}
          className="flex-1 justify-center"
        />
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          {/* Language selector - same visual as DesktopFilterCategories (xl only), static/non-interactive */}
          <div className="hidden xl:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 pointer-events-none">
            {localeCode === 'FI' && <span className="text-xs">FI</span>}
            {localeCode === 'SV' && <span className="text-xs">SV</span>}
            {localeCode === 'EN' && <span className="text-xs">EN</span>}
          </div>
          <button
            type="button"
            className={`flex items-center gap-2 py-1.5 rounded-full bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 cursor-pointer ${hasUser ? 'pl-1 pr-2' : 'px-2'}`}
            aria-label={staticStrings.menuLabel}
          >
            {hasUser ? (
              <>
                <div className="flex-shrink-0 overflow-hidden rounded-full">
                  <Avatar
                    src={userAvatarUrl ?? undefined}
                    size={32}
                    name={userName ?? 'User'}
                  />
                </div>
                <Menu className="w-5 h-5 text-gray-700 dark:text-gray-300 flex-shrink-0" />
              </>
            ) : (
              <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            )}
          </button>
        </div>
      </nav>
      {/* Second row: facet pills (same as DesktopFilterCategories TopFilters); logo on left on mobile via CSS lg:hidden */}
      {facets && Object.keys(facets).length > 0 && pathname && indexName && (
        <StaticTopFilters
          facets={facets}
          refinementList={refinementList}
          pathname={pathname}
          indexName={indexName}
          query={query ?? ''}
          locale={normalizedLocale}
          logoTooltip={staticStrings.logoTooltip}
        />
      )}
    </header>
  );
}

/** Raw facets from StaticSRPData (value, count; optional label/isRefined from Algolia). */
type RawFacetsData = Record<string, Array<{ value: string; label?: string; count: number; isRefined?: boolean }>>;

/**
 * Static SRP header (interactive tree): uses IntlProvider. Same layout as StaticSRPHeaderWithStrings.
 * When facetsData/pathname/indexName are provided, renders the second row (StaticTopFilters) from initial Algolia data.
 */
export function StaticSRPHeader({
  locale,
  query,
  facetsData,
  refinementList = {},
  pathname,
  indexName,
}: {
  locale: string;
  query?: string;
  facetsData?: RawFacetsData;
  refinementList?: Record<string, string[]>;
  pathname?: string;
  indexName?: string;
}): React.ReactElement {
  const intl = useIntl();
  const categories = React.useMemo(
    () => createFilterCategories(intl, {}),
    [intl]
  );
  const searchPlaceholder = intl.formatMessage({
    id: SEARCH_PLACEHOLDER_ID,
    defaultMessage: 'Search jobs, language, or city',
    description: 'Placeholder text for search input box',
  });

  const facetsForTopFilters: SrpFacetsData | undefined = React.useMemo(() => {
    if (!facetsData || !pathname || !indexName) return undefined;
    const out: SrpFacetsData = {};
    for (const [attr, items] of Object.entries(facetsData)) {
      out[attr] = items.map((item) => ({
        value: item.value,
        label: item.label ?? item.value,
        count: item.count,
        selected: item.isRefined ?? (refinementList[attr] ?? []).includes(item.value),
      }));
    }
    return out;
  }, [facetsData, refinementList]);

  return (
    <header
      className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-[45]"
      role="banner"
    >
      <nav
        className="hidden lg:flex items-center gap-3 h-14 min-h-14 py-3 px-4 xl:px-20 md:px-10 border-b border-gray-200 dark:border-gray-700"
        aria-label="Main navigation"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Logo intl={intl} locale={locale} variant="horizontal" />
          <Logo intl={intl} locale={locale} variant="compact" />
          <div className="min-w-0 flex-1 max-w-lg xl:max-w-3xl w-full">
            <StaticSearchBox query={query ?? ''} placeholder={searchPlaceholder} />
          </div>
        </div>
        <FilterCategoryButtons
          intl={intl}
          categories={categories}
          activeCategory={null}
          onCategoryClick={() => {}}
          className="flex-1 justify-center"
        />
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <button
            type="button"
            className="flex items-center gap-2 py-1.5 px-2 rounded-full bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            aria-label={intl.formatMessage({
              id: 'navigation.menu',
              defaultMessage: 'Menu',
            })}
          >
            <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
          </button>
        </div>
      </nav>
      {facetsForTopFilters && Object.keys(facetsForTopFilters).length > 0 && pathname && indexName && (
        <StaticTopFilters
          facets={facetsForTopFilters}
          refinementList={refinementList}
          pathname={pathname}
          indexName={indexName}
          query={query ?? ''}
          locale={locale}
          logoTooltip={intl.formatMessage({
            id: 'tooltip.logo',
            defaultMessage: 'Go to homepage and clear search',
            description: 'Tooltip for logo in navigation',
          })}
        />
      )}
    </header>
  );
}
